import type { PayloadAction } from "@reduxjs/toolkit";
import { arrayToMap } from "../../../helpers/catalogueHelpers";
import { convertArrayToObject } from "../../../helpers/commonHelpers";
import { set } from "lodash";
import { CATEGORY_TYPE } from "../../../data/constants";
import { othersDataTypes } from "../../../facade/ServiceFacade";

/**
 * Shared parts of catalogueSlice — plan §5.2. The biggest slice in the migration: 43 web
 * reducers vs 53 mobile, of which **19 are byte-identical**, 4 genuinely diverge, 20 are
 * web-only and 30 mobile-only.
 *
 * ⚠️ The divergence here is STRUCTURAL, not incidental, and the ledger's earlier analysis holds:
 * mobile fetches charges/taxes/brands/channels through ~24 per-section reducers
 * (`getChargesData`/`Success`/`Fail`, `getTaxAreaData`/...), while web parses all of it INLINE
 * inside `getCatalogueDataSuccess` and keeps no-op `*CallComplete` actions instead. Neither
 * shape is reducible to the other, and no dead flag is load-bearing in any divergent reducer —
 * this was checked when the dead-state sweep was run.
 *
 * All three intra-core imports are BARE FOLDER imports, which is correct for a `shared.ts`
 * (hazard 16's table): the file runs on both platforms, so the bundler must pick each variant.
 * That matters most for `arrayToMap`, which genuinely differs — web returns `null` for an empty
 * input, mobile `{}` — and CLAUDE.md calls that difference load-bearing.
 *
 * Authored per platform instead:
 * - `getCatalogueDataSuccess` — web additionally derives `channelData`, `charges`, `taxTypes`,
 *   `taxArea`, `categoryGroup`, `brandsData`, `channelvos` and `channelDef` from the one
 *   payload; mobile derives only the category/schedule fields and leaves the rest to its
 *   per-section reducers.
 * - `getOptionSetsDataSuccess` — web stores the raw array, mobile stores `arrayToMap(..., 'mid')`.
 *   This is also why `optionSetData` is NOT in `SharedCatalogState`: it is `[]` on web and `{}`
 *   on mobile, in both type and default.
 * - `getBizBrandsDataFail` — identical logic, but web writes `!!state.bizBrandsData?.length`
 *   where mobile writes the un-negated `state.bizBrandsData?.length`. Same runtime result;
 *   preserved verbatim rather than unified, per "changes no behaviour".
 * - `getItemTagSuccess` — mobile also sets `tagsPriorityArr` via `setPriorityForItemTags`.
 * - web's 18 remaining extra reducers (11 of the 12 `*CallComplete` no-ops, most-sold items,
 *   `getSelectedDealData`, `updateCatalogSlice`, `channelItemMapDetail`) and its
 *   `userLogout`/`supportLogout` reset; mobile registers NO extraReducers and resets through
 *   its own `resetCatalogue` action instead.
 *   🔴 **`updateCatalogueData` and `catalogueDataCallComplete` were two of those web-only
 *   reducers and are now SHARED** (2026-08-23) — they are dispatched by the unified
 *   `getCatalogueDataFn`, and a shared caller cannot dispatch a one-sided action. See their
 *   docstrings at the end of `sharedReducers`.
 * - mobile's 30 extra reducers (the per-section fetch triples, pinned channels, the
 *   delivery/dine maps, the visited-API tracking and `resetCatalogue`).
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger.
 */
/** `itemTagId` -> tag, as `getItemTagSuccess` stores it on both platforms. */
export interface ItemTagMap {
  [tagId: string]: { mid?: string; name?: string; [key: string]: any };
}

export interface SharedCatalogState {
  isCatalogueFetching: boolean;
  isOptionSetFetching: boolean;
  isPOSSettingsFetching: boolean;
  isDiscountsFetching: boolean;
  isChargesFetching: boolean;
  catalogueData: {};
  categoriesWithCatalogType: any[];
  categoriesWithInventoryType: any[];
  channelData: [];
  categoryData: {};
  catalogOnlyCategoryData: {};
  menuData: {};
  // Widened here ONLY so each platform interface can redeclare it with its app's exact
  // spelling (web `{}`, mobile `{ [key: string]: any }`). Shared reducers still write it.
  posSettings: any;
  discounts: {};
  charges: {};
  taxArea: {};
  taxTypes: [];
  channelDef: [];
  channelvos: [];
  categoryGroup: [];
  brandsData: [];
  bizBrandsData: [];
  isTaxAreaFetching: boolean;
  isTaxTypeFetching: boolean;
  isChannelDefFetching: boolean;
  isChannelvosFetching: boolean;
  isCategoryGroupFetching: boolean;
  isBizBrandsFetching: boolean;
  isBrandsFetching: boolean;
  currentSelectedMode: string;
  isFetchingItemTag: boolean;
  /**
   * 🔴 **A `mid`-keyed MAP, not an array** — `[]` only until the fetch lands.
   *
   * Both apps declared this `[]`, and that declaration is simply wrong: `getItemTagSuccess`
   * writes `arrayToMap(response, "mid")` on BOTH platforms, and every consumer reads it as a
   * map (`tagsData?.[tagId].name` in both apps' `checkTag`, `Object.keys` in
   * `setPriorityForItemTags`, `for (const key in tagsData)` in web's
   * `findMidOfChefRecommended`). The union is the honest type: `never[]` keeps the `[]`
   * INITIAL VALUE and mobile's `resetCatalogue` reset exactly as the apps wrote them — this
   * is a type correction, not a behaviour change.
   *
   * Ledger hazard 38, error 4. This was never a platform divergence: it failed only in the
   * native program because `redux-store/index.desktop.ts` annotates its store `any`, so the
   * desktop program cannot see ANY store-shape error (81 of them surface if that `any` is
   * removed).
   */
  itemTags: ItemTagMap | never[];
  // Same reason: web declares `[]`, mobile `any[]`.
  tagsPriorityArr: any[];
  menuToItemDataMap: {};
  // Same reason: web declares `[]`, mobile `Record<string, any>[]`.
  scheduleData: any[];
}

/**
 * ⚠️ Deliberately omits `categoryData` — the platforms initialise it differently (web `{}`,
 * mobile `{ channelvos: [] }`), so each `index.*.ts` must supply its own (hazard 7's shape,
 * third use). `optionSetData` is absent from the interface entirely, not just this default,
 * because its TYPE differs too.
 */
export const sharedInitialState: Omit<SharedCatalogState, "categoryData"> = {
  isCatalogueFetching: false,
  isOptionSetFetching: false,
  isPOSSettingsFetching: false,
  isDiscountsFetching: false,
  isChargesFetching: false,
  catalogueData: {},
  categoriesWithCatalogType: [],
  categoriesWithInventoryType: [],
  channelData: [],
  catalogOnlyCategoryData: {},
  menuData: {},
  posSettings: {},
  discounts: {},
  charges: {},
  taxArea: {},
  taxTypes: [],
  channelDef: [],
  channelvos: [],
  categoryGroup: [],
  brandsData: [],
  bizBrandsData: [],
  isTaxTypeFetching: false,
  isChannelDefFetching: false,
  isTaxAreaFetching: false,
  isChannelvosFetching: false,
  isCategoryGroupFetching: false,
  isBizBrandsFetching: false,
  isBrandsFetching: false,
  currentSelectedMode: "",
  isFetchingItemTag: false,
  itemTags: [],
  tagsPriorityArr: [],
  menuToItemDataMap: {},
  scheduleData: [],
};

export const sharedReducers = {
  getCatalogueData: (state: SharedCatalogState) => {
    state.isCatalogueFetching = true;
  },
  getCatalogueDataFail: (state: SharedCatalogState) => {
    state.isCatalogueFetching = false;
  },
  setChannelDataSuccess: (state: SharedCatalogState, action: PayloadAction<any>) => {
    state.channelData = action.payload;
  },
  setCatagoryDataSuccess: (state: SharedCatalogState, action: PayloadAction<any>) => {
    const categories = action.payload?.categories || [];
    state.categoryData = convertArrayToObject(categories, "mid");
    const filteredCatalogCategories = categories.filter((category: any) =>
      category?.categoryTypes?.includes(CATEGORY_TYPE.CATALOG)
    );
    state.catalogOnlyCategoryData = convertArrayToObject(filteredCatalogCategories, "mid");
  },
  setMenuDataSuccess: (state: SharedCatalogState, action: PayloadAction<any>) => {
    state.menuData = arrayToMap(action.payload?.menus || [], "mid");
  },
  getOptionSetsData: (state: SharedCatalogState) => {
    state.isOptionSetFetching = true;
  },
  getOptionSetsDataFail: (state: SharedCatalogState) => {
    state.isOptionSetFetching = false;
  },
  getPOSSettingsData: (state: SharedCatalogState, action: PayloadAction<any>) => {
    state.isPOSSettingsFetching = true;
  },
  getPOSSettingsDataSuccess: (state: SharedCatalogState, action: PayloadAction<any>) => {
    state.posSettings = action.payload.response;
    state.isPOSSettingsFetching = false;
  },
  getPOSSettingsDataFail: (state: SharedCatalogState) => {
    state.isPOSSettingsFetching = false;
  },
  getDiscountsData: (state: SharedCatalogState) => {
    state.isDiscountsFetching = true;
  },
  getDiscountsDataSuccess: (state: SharedCatalogState, action: PayloadAction<any>) => {
    state.isDiscountsFetching = false;
    state.discounts = arrayToMap(action.payload.response || [], "mid");
  },
  getDiscountsDataFail: (state: SharedCatalogState) => {
    state.isDiscountsFetching = false;
  },
  getBizBrandsData: (state: SharedCatalogState) => {
    state.isBizBrandsFetching = true;
  },
  getBizBrandsDataSuccess: (state: SharedCatalogState, action: PayloadAction<any>) => {
    state.isBizBrandsFetching = false;
    state.bizBrandsData = action.payload.response;
  },
  // ⚠️ The console.log is in BOTH apps, identically, so it is not a divergence driver and
  // standing rule 2(a) does not fire. Preserved verbatim; flagged as a cleanup candidate.
  updateCurrentSelectedMode: (state: SharedCatalogState, action: PayloadAction<any>) => {
    console.log("action: ", action);
    state.currentSelectedMode = action.payload;
  },
  getItemTag: (state: SharedCatalogState) => {
    state.isFetchingItemTag = true;
  },
  storeItemTagsPriority: (state: SharedCatalogState, action: PayloadAction<any>) => {
    state.tagsPriorityArr = action.payload;
  },
  setMenuToItemDataMap: (state: SharedCatalogState, action: PayloadAction<any>) => {
    state.menuToItemDataMap = action.payload || {};
  },
  /**
   * No-op marker: "the catalogue data call has completed". Nothing reads state here — the point
   * is the ACTION TYPE, which listeners elsewhere key off.
   *
   * 🔴 **Moved here from `index.desktop.ts` on 2026-08-23, so it exists on both platforms.** It
   * is dispatched by the now-shared `getCatalogueDataFn` (`facade/ServiceFacade/shared.ts`), and
   * a `shared.ts` cannot dispatch an action only one platform defines. The other eleven
   * `*CallComplete` no-ops stay one-sided — only this one has a shared caller.
   */
  catalogueDataCallComplete: (state: SharedCatalogState) => {
    // this action is to tell that the catalog data call has completed
  },
  /**
   * Fold ONE section of the offline "others" catalogue back into state: stash the raw payload
   * under `catalogueData[key]`, then run that section's `transform` onto its own state field.
   *
   * 🔴 **Moved here from `index.desktop.ts` on 2026-08-23 together with its caller.** It had been
   * one-sided because only one platform's catalogue refetch ran through the generic loop; the
   * other's copy of that loop dispatched this action without its slice ever defining it, which
   * is half of the defect the shared `getCatalogueDataFn` repairs. Every field the transforms
   * write (`brandsData`, `charges`, `taxArea`, `taxTypes`, `channelvos`, `channelDef`,
   * `categoryGroup`, `categoryData`, `menuData`, `scheduleData`, `channelData`) is declared in
   * `SharedCatalogState`, so this is type-safe on both — checked field-by-field, and against the
   * per-section reducers that write the same fields on the platform that has them.
   *
   * ⚠️ `othersDataTypes` comes from `facade/ServiceFacade`, which imports action creators back
   * out of this slice. The cycle is pre-existing and safe — see that file's header.
   */
  updateCatalogueData: (state: SharedCatalogState, action: PayloadAction<any>) => {
    const { key, data } = action.payload;
    set(state.catalogueData, key, data);

    const config = othersDataTypes.find((item) => item.key === key);
    if (config) {
      (state as any)[config.type] = config.transform(data, state);
    }
  },
};
